import { OutputTimestampTimebase, type AudioTimebase } from "./audio-timebase";

export interface ScheduleTickerOptions {
  /** Fenêtre d'anticipation, en secondes. */
  scheduleAheadTime?: number;
  /** Période de réveil, en millisecondes. */
  scheduleInterval?: number;
}

export interface ScheduleEventDetail {
  /** L'heure qu'il est sur l'horloge du contexte audio. */
  audioTime: number;
  /** `audioTime + scheduleAheadTime` : la fin de la fenêtre à remplir. */
  scheduleUntil: number;
}

export type ScheduleCallback = (audioTime: number, scheduleUntil: number) => void;

/**
 * Le ticker d'ordonnancement : il réveille périodiquement ceux qui doivent
 * remettre des événements à l'audio, avec la fenêtre à remplir.
 *
 * Il travaille sur l'horloge du contexte audio, et c'est délibéré — c'est la
 * seule qui soit exacte à l'échantillon, et une note doit sonner à son heure.
 * La **position**, elle, ne le suit pas : voir `PositionClock`.
 *
 * # Rien ne s'ordonnance sur une horloge figée
 *
 * Tant que le contexte n'a pas commencé à rendre — avant le premier geste de
 * l'utilisateur, typiquement — aucune fenêtre n'est diffusée. Sans cette garde,
 * les notes s'empileraient à des dates déjà passées et partiraient toutes
 * ensemble au réveil du contexte. La boucle continue de tourner : c'est elle
 * qui verra le contexte repartir.
 */
export class ScheduleTicker extends EventTarget {
  private schedulerId?: ReturnType<typeof setTimeout>;
  private isRunning = false;
  private readonly scheduleAheadTime: number;
  private readonly scheduleInterval: number;
  private readonly callbackMap = new WeakMap<ScheduleCallback, EventListener>();
  private readonly boundScheduler: () => void;

  /** Le pont vers l'horloge de position, tenu ici parce que le contexte l'est. */
  readonly timebase: AudioTimebase;

  constructor(private ctx: AudioContext, options: ScheduleTickerOptions = {}) {
    super();
    this.scheduleAheadTime = options.scheduleAheadTime ?? 0.1;
    this.scheduleInterval = options.scheduleInterval ?? 25;
    this.boundScheduler = () => this.scheduler();
    this.timebase = new OutputTimestampTimebase(ctx);
  }

  start() {
    if (this.isRunning) {
      return;
    }

    // Lancé sans attendre, et sans bruit s'il est refusé : ce `start()` part
    // du constructeur du studio, donc avant tout geste. Safari rejette alors
    // ce réveil-là — c'est normal, `unlockAudioOnGesture` s'en charge au
    // premier geste — mais la promesse sans `catch` remontait en rejet non
    // traité dans la console d'un appareil qui n'a rien d'autre à dire.
    void this.ctx.resume().catch(() => undefined);
    this.isRunning = true;
    this.scheduler();
  }

  stop() {
    if (!this.isRunning) {
      return;
    }

    this.ctx.suspend();
    this.isRunning = false;

    if (this.schedulerId !== undefined) {
      clearTimeout(this.schedulerId);
      this.schedulerId = undefined;
    }
  }

  destroy() {
    this.stop();
  }

  /** L'heure sur l'horloge du contexte audio. Ce n'est pas une position. */
  get audioTime(): number {
    return this.ctx.currentTime;
  }

  getAudioContext(): AudioContext {
    return this.ctx;
  }

  register(callback: ScheduleCallback) {
    // Un second `register` du même rappel créait un second écouteur, que
    // `unregister` — qui ne connaît que le dernier — ne retirait jamais.
    if (this.callbackMap.has(callback)) {
      return;
    }

    const listener = ((e: CustomEvent<ScheduleEventDetail>) => {
      callback(e.detail.audioTime, e.detail.scheduleUntil);
    }) as EventListener;

    this.callbackMap.set(callback, listener);
    this.addEventListener("schedule", listener);
  }

  unregister(callback: ScheduleCallback) {
    const listener = this.callbackMap.get(callback);

    if (listener) {
      this.removeEventListener("schedule", listener);
      this.callbackMap.delete(callback);
    }
  }

  private scheduler() {
    if (this.timebase.isRendering) {
      const audioTime = this.ctx.currentTime;
      const scheduleUntil = audioTime + this.scheduleAheadTime;

      this.dispatchEvent(
        new CustomEvent<ScheduleEventDetail>("schedule", {
          detail: { audioTime, scheduleUntil },
        }),
      );
    }

    if (this.isRunning) {
      this.schedulerId = setTimeout(this.boundScheduler, this.scheduleInterval);
    }
  }
}
